'use client';

import React from 'react';
import { useRole } from '@/context/RoleContext';
import AppLayout from '@/components/AppLayout';
import DashboardHeader from './components/DashboardHeader';
import KPIBentoGrid from './components/KPIBentoGrid';
import ComplianceAlertBanner from './components/ComplianceAlertBanner';
import DashboardCharts from './components/DashboardCharts';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import { ShieldOff } from 'lucide-react';

function AccessDenied({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-center px-6">
      <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center">
        <ShieldOff size={24} className="text-muted-foreground" />
      </div>
      <div>
        <h2 className="text-lg font-600 text-foreground mb-1">Access Restricted</h2>
        <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { permissions, currentUser } = useRole();

  if (!permissions.canViewDashboard) {
    return (
      <AppLayout>
        <AccessDenied message={`The dashboard is not available for your role (${currentUser.roleLabel}). Use the navigation to access your permitted areas.`} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6 space-y-6">
        <DashboardHeader />
        {permissions.canViewComplianceAlerts && <ComplianceAlertBanner />}
        {permissions.canViewKPIs && <KPIBentoGrid />}
        {permissions.canViewCharts && <DashboardCharts />}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {permissions.canViewActivityFeed && (
            <div className={permissions.canViewQuickActions ? 'xl:col-span-2' : 'xl:col-span-3'}>
              <ActivityFeed />
            </div>
          )}
          {permissions.canViewQuickActions && (
            <div>
              <QuickActions />
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}